import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { RefreshRounded } from '@material-ui/icons';
import moment from 'moment';
import apiCaller from '../../../../utils/apiCallerOutstanding';
import myFirebase from '../../../../config/config';
import logger from '../../../../utils/logger';
import { getTimeElapsed } from '../../../../utils/convertDateFormat';

const useStyles = makeStyles({
  root: {
    minWidth: 275,
    marginTop: '20px',
    textAlign: 'left',
  },
  emptyState: {
    fontSize: '14px',
  },
  pos: {
    marginBottom: 1,
    fontSize: '14px',
  },
  commentBox: {
    maxHeight: '300px',
    overflowY: 'scroll',
    scrollbarWidth: 'thin',
    padding: '8px',
  },
  rajdhaniFont: {
    fontFamily: 'Rajdhani',
    margin: 0,
  },
  titleSection: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '5px',
    alignItems: 'center',
  },
  activityNotes: {
    marginLeft: '20px',
    fontSize: '12px',
    fontWeight: 300,
    marginBottom: '1px',
  },
  card: {
    borderRadius: 12,
    border: '1px solid #E5E7EB',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  ticketNo: {
    fontSize: 16,
    fontWeight: 500,
  },
  chipContainer: {
    display: 'flex',
    gap: 8,
  },
  chipRoot: {
    borderRadius: 4,
    height: 22,
  },
  chipLabel: {
    fontSize: 11,
    fontWeight: 600,
    paddingLeft: 6,
    paddingRight: 6,
  },
  internalChip: {
    backgroundColor: '#FCD21F',
    color: '#0A0B09',
    fontWeight: 600,
  },
  externalChip: {
    backgroundColor: '#0A0B09',
    color: '#FCD21F',
    fontWeight: 600,
  },
  chip: {
    fontWeight: 600,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 12,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  metaText: {
    color: '#374151',
    fontSize: 14,
  },
  dot: {
    fontSize: 18,
    lineHeight: 0,
  },
});
const getStatusColor = status => {
  switch (status) {
    case 'closed':
      return {
        text: '#0A0B09',
        background: '#DADADA',
      };
    case 'to_do':
      return {
        text: '#0A0B09',
        background: '#FEF1BC',
      };
    case 'in_progress':
      return {
        text: '#040C22',
        background: '#BBD6BE',
      };
    default:
      return {
        text: '#0A0B09',
        background: '#EAEEFF',
      };
  }
};
const getPriorityColor = priority => {
  switch (priority) {
    case 'high':
      return '#E58282';
    case 'medium':
      return '#FFD8B3';
    case 'low':
      return '#BBD6BE';
    default:
      return '#FFF2E6';
  }
};
const formatString = str =>
  str
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.toUpperCase())
    .join(' ');
export default function LinkedTickets({ transactionId }) {
  const [ticketsData, setTicketsData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const classes = useStyles();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getTicketsData = async () => {
    setFetching(true);
    const userDetails = myFirebase.auth().currentUser;
    const token = await userDetails.getIdToken();
    const endPoint = `pipe/v1/linked-tickets?trip_id=${transactionId}`;
    if (token) {
      await apiCaller(endPoint, {}, `Bearer ${token}`, 'nh-base', 'get')
        .then(response => {
          setTicketsData(response?.data?.Data || []);
          setFetching(false);
        })
        .catch(error => {
          setFetching(false);
          setTicketsData([]);
          logger.error(error);
        });
    } else {
      setFetching(false);
    }
  };

  useEffect(() => {
    getTicketsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  return (
    <Card className={classes.root}>
      <CardHeader
        title={
          <div className={classes.titleSection}>
            <p className={classes.rajdhaniFont}>Lobb Care Tickets</p>
            <IconButton
              disabled={fetching}
              size="small"
              onClick={() => getTicketsData()}
            >
              <RefreshRounded />
            </IconButton>
          </div>
        }
      />
      <Divider />
      {fetching ? (
        <CircularProgress size={20} />
      ) : (
        <CardContent className={classes.commentBox}>
          {ticketsData?.length === 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Typography
                className={classes.emptyState}
                color="textSecondary"
                gutterBottom
              >
                No Tickets Available
              </Typography>
            </div>
          ) : null}
          {ticketsData?.length > 0 ? (
            <div
              style={{
                margin: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              {ticketsData?.map(ticketItem => (
                <Card className={classes.card} key={ticketItem?.id}>
                  <CardContent>
                    {/* Top row */}
                    <Box className={classes.topRow}>
                      <Typography href="#" className={classes.ticketNo}>
                        {ticketItem?.serial || '-'}
                      </Typography>

                      <Box className={classes.chipContainer}>
                        <Chip
                          classes={{
                            root: classes.chipRoot,
                            label: classes.chipLabel,
                          }}
                          label={
                            ticketItem?.is_internal ? 'INTERNAL' : 'EXTERNAL'
                          }
                          className={
                            ticketItem?.is_internal
                              ? classes.internalChip
                              : classes.externalChip
                          }
                        />
                        <Chip
                          classes={{
                            root: classes.chipRoot,
                            label: classes.chipLabel,
                          }}
                          label={ticketItem?.priority?.toUpperCase() || '-'}
                          className={classes.chip}
                          style={{
                            backgroundColor: getPriorityColor(
                              ticketItem?.priority
                            ),
                            color: '#0A0B09',
                          }}
                        />
                        <Chip
                          classes={{
                            root: classes.chipRoot,
                            label: classes.chipLabel,
                          }}
                          label={formatString(ticketItem?.status || '-')}
                          className={classes.chip}
                          style={{
                            backgroundColor: getStatusColor(ticketItem?.status)
                              ?.background,
                            color: getStatusColor(ticketItem?.status)?.text,
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Title */}
                    <Typography className={classes.title}>
                      {ticketItem?.title || '-'}
                    </Typography>

                    {/* Meta info */}
                    <Box className={classes.metaRow}>
                      <Tooltip title="Assigned To">
                        <Typography className={classes.metaText}>
                          {ticketItem?.assignee || '-'}
                        </Typography>
                      </Tooltip>
                      <span className={classes.dot}>•</span>
                      <Tooltip title="Branch Name">
                        <Typography className={classes.metaText}>
                          {ticketItem?.branch || '-'}
                        </Typography>
                      </Tooltip>
                      <span className={classes.dot}>•</span>
                      <Tooltip title="Created Date">
                        <Typography className={classes.metaText}>
                          {ticketItem?.created_date
                            ? moment(ticketItem?.created_date).format(
                                'DD/MM/yyyy'
                              )
                            : '-'}
                        </Typography>
                      </Tooltip>
                      <span className={classes.dot}>•</span>
                      <Typography className={classes.metaText}>
                        🕒{' '}
                        {ticketItem?.created_date
                          ? getTimeElapsed(ticketItem?.created_date)
                          : '-'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : null}
        </CardContent>
      )}
    </Card>
  );
}
